import { Platform } from 'react-native';
import * as ImageManipulator from 'expo-image-manipulator';
import * as FileSystem from 'expo-file-system';
import { getItem, setItem, deleteItem } from '../utils/storage';

let agent: any = null;
let BskyAgent: any = null;
let RichText: any = null;

// Lazy load @atproto/api to avoid webpack issues
const loadAtprotoApi = async () => {
  if (BskyAgent && RichText) return { BskyAgent, RichText };
  
  try {
    const module = await import('@atproto/api');
    BskyAgent = module.BskyAgent;
    RichText = module.RichText;
    console.log('✅ @atproto/api loaded:', { hasBskyAgent: !!BskyAgent, hasRichText: !!RichText });
    return { BskyAgent, RichText };
  } catch (e) {
    console.warn('⚠️ Failed to load @atproto/api:', e);
    return { BskyAgent: null, RichText: null };
  }
};

// Manual facet detection for when RichText is not available
const detectFacetsManually = (text: string) => {
  const facets: any[] = [];
  const encoder = new TextEncoder();
  const bytes = encoder.encode(text);
  
  // Detect URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  let match;
  while ((match = urlRegex.exec(text)) !== null) {
    const url = match[1];
    const startIndex = match.index;
    const endIndex = startIndex + url.length;
    
    // Convert to byte positions
    const byteStart = encoder.encode(text.substring(0, startIndex)).length;
    const byteEnd = byteStart + encoder.encode(url).length;
    
    facets.push({
      index: {
        byteStart,
        byteEnd,
      },
      features: [
        {
          $type: 'app.bsky.richtext.facet#link',
          uri: url,
        },
      ],
    });
  }
  
  // Detect hashtags
  const hashtagRegex = /(^|\s)(#[a-zA-Z0-9_]+)/g;
  while ((match = hashtagRegex.exec(text)) !== null) {
    const hashtag = match[2];
    const startIndex = match.index + match[1].length;
    const endIndex = startIndex + hashtag.length;
    
    // Convert to byte positions
    const byteStart = encoder.encode(text.substring(0, startIndex)).length;
    const byteEnd = byteStart + encoder.encode(hashtag).length;
    
    facets.push({
      index: {
        byteStart,
        byteEnd,
      },
      features: [
        {
          $type: 'app.bsky.richtext.facet#tag',
          tag: hashtag.substring(1), // Remove the # prefix
        },
      ],
    });
  }
  
  // Detect mentions
  const mentionRegex = /(^|\s)(@[a-zA-Z0-9._-]+)/g;
  while ((match = mentionRegex.exec(text)) !== null) {
    const mention = match[2];
    const startIndex = match.index + match[1].length;
    const endIndex = startIndex + mention.length;
    
    // Convert to byte positions
    const byteStart = encoder.encode(text.substring(0, startIndex)).length;
    const byteEnd = byteStart + encoder.encode(mention).length;
    
    facets.push({
      index: {
        byteStart,
        byteEnd,
      },
      features: [
        {
          $type: 'app.bsky.richtext.facet#mention',
          did: mention.substring(1), // This should be a DID, but we'll use the handle for now
        },
      ],
    });
  }
  
  return facets.length > 0 ? facets : undefined;
};

// Parse AT URI: at://did:plc:xxx/app.bsky.feed.post/rkey -> { repo, collection, rkey }
const parseAtUri = (uri: string) => {
  if (!uri || !uri.startsWith('at://')) {
    return { repo: '', collection: '', rkey: '' };
  }
  const parts = uri.slice(5).split('/');
  return {
    repo: parts[0] || '',
    collection: parts[1] || '',
    rkey: parts[2] || ''
  };
};
async function ensureAgent() {
  if (agent) {
    return agent;
  }
  
  // Load @atproto/api dynamically
  const { BskyAgent: LoadedBskyAgent, RichText: LoadedRichText } = await loadAtprotoApi();
  
  // If BskyAgent is available, use it
  if (LoadedBskyAgent) {
    console.log('ensureAgent: Using BskyAgent');
    BskyAgent = LoadedBskyAgent;
    RichText = LoadedRichText;
    agent = new BskyAgent({ service: 'https://bsky.social' });
    return agent;
  }
  
  // Otherwise, create SimpleBrowserAgent fallback
  console.log('ensureAgent: Creating SimpleBrowserAgent fallback');
  
  class SimpleBrowserAgent {
    session: any = null;
    service: string;
    
    constructor(opts: any) {
      this.service = opts?.service || 'https://bsky.social';
    }
    
    async login(opts: { identifier: string; password: string }) {
      const res = await fetch(this.service + '/xrpc/com.atproto.server.createSession', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ identifier: opts.identifier, password: opts.password }),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error('Login failed: ' + res.status + ' ' + txt);
      }
      const data = await res.json();
      this.session = { accessJwt: data.accessJwt, refreshJwt: data.refreshJwt, did: data.did, handle: data.handle };
      return { data };
    }
    
    get did() {
      return this.session?.did;
    }

    // Proxy-like access for app.bsky.*
    get app() {
      return {
        bsky: {
          actor: {
            getPreferences: () => this._fetch('app.bsky.actor.getPreferences'),
            putPreferences: (params: any) => this._fetch('app.bsky.actor.putPreferences', params, 'POST'),
            searchActors: (params: any) => this._fetch('app.bsky.actor.searchActors', params),
          },
          feed: {
            getFeedGenerators: (params: any) => this._fetch('app.bsky.feed.getFeedGenerators', params),
            getFeed: (params: any) => this._fetch('app.bsky.feed.getFeed', params),
            getListFeed: (params: any) => this._fetch('app.bsky.feed.getListFeed', params),
            searchPosts: (params: any) => this._fetch('app.bsky.feed.searchPosts', params),
          },
          graph: {
            getLists: (params: any) => this._fetch('app.bsky.graph.getLists', params),
            getList: (params: any) => this._fetch('app.bsky.graph.getList', params),
          },
          unspecced: {
            getPopularFeedGenerators: (params: any) => this._fetch('app.bsky.unspecced.getPopularFeedGenerators', params),
          }
        }
      };
    }

    async _fetch(endpoint: string, params: any = {}, method: string = 'GET', service?: string) {
      let url = (service || this.service) + '/xrpc/' + endpoint;
      let options: any = {
        method,
        headers: {
          ...(this.session?.accessJwt ? { authorization: `Bearer ${this.session.accessJwt}` } : {}),
        },
      };

      if (method === 'GET') {
        const searchParams = new URLSearchParams();
        for (const key in params) {
          if (params[key] !== undefined && params[key] !== null) {
            if (Array.isArray(params[key])) {
              params[key].forEach((v: any) => searchParams.append(key, v));
            } else {
              searchParams.append(key, params[key]);
            }
          }
        }
        const qs = searchParams.toString();
        if (qs) url += '?' + qs;
      } else {
        options.headers['content-type'] = 'application/json';
        options.body = JSON.stringify(params);
      }

      console.log(`🌐 [_fetch] ${method} ${url}`);
      console.log('🌐 [_fetch] Params:', params);
      console.log('🌐 [_fetch] Has auth:', !!this.session?.accessJwt);

      const res = await fetch(url, options);
      console.log(`🌐 [_fetch] Response status: ${res.status} ${res.statusText}`);
      
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        console.error(`❌ [_fetch] Failed: ${res.status} ${txt}`);
        throw new Error(`${endpoint} failed: ${res.status} ${txt}`);
      }
      
      // Handle empty response (common in delete operations)
      const text = await res.text();
      console.log('🌐 [_fetch] Response body:', text || '(empty)');
      const data = text ? JSON.parse(text) : {};
      console.log('✅ [_fetch] Success:', data);
      return { data };
    }

    async getTimeline(params: any) {
      return this._fetch('app.bsky.feed.getTimeline', params);
    }
    
    async getPostThread(params: any) {
      return this._fetch('app.bsky.feed.getPostThread', params);
    }
    
    async getFeed(params: any) {
      return this._fetch('app.bsky.feed.getFeed', params);
    }
    
    async getListFeed(params: any) {
      return this._fetch('app.bsky.feed.getListFeed', params);
    }
    
    async getPosts(params: any) {
      return this._fetch('app.bsky.feed.getPosts', params);
    }
    
    async listNotifications(params: any) {
      return this._fetch('app.bsky.notification.listNotifications', params);
    }
    
    async like(uri: string, cid: string) {
      return this._fetch('com.atproto.repo.createRecord', {
        repo: this.session.did,
        collection: 'app.bsky.feed.like',
        record: { subject: { uri, cid }, createdAt: new Date().toISOString() }
      }, 'POST');
    }
    
    async deleteLike(uri: string) {
      const parts = uri.split('/');
      const rkey = parts[parts.length - 1];
      return this._fetch('com.atproto.repo.deleteRecord', {
        repo: this.session.did,
        collection: 'app.bsky.feed.like',
        rkey
      }, 'POST');
    }
    
    async repost(uri: string, cid: string) {
      return this._fetch('com.atproto.repo.createRecord', {
        repo: this.session.did,
        collection: 'app.bsky.feed.repost',
        record: { subject: { uri, cid }, createdAt: new Date().toISOString() }
      }, 'POST');
    }
    
    async deleteRepost(uri: string) {
      const parts = uri.split('/');
      const rkey = parts[parts.length - 1];
      return this._fetch('com.atproto.repo.deleteRecord', {
        repo: this.session.did,
        collection: 'app.bsky.feed.repost',
        rkey
      }, 'POST');
    }
    
    async post(record: any) {
      return this._fetch('com.atproto.repo.createRecord', {
        repo: this.session.did,
        collection: 'app.bsky.feed.post',
        record
      }, 'POST');
    }
    
    async getProfile(params: any) {
      return this._fetch('app.bsky.actor.getProfile', params);
    }
    
    async getAuthorFeed(params: any) {
      return this._fetch('app.bsky.feed.getAuthorFeed', params);
    }
    
    async getFollows(params: any) {
      return this._fetch('app.bsky.graph.getFollows', params);
    }
    
    async getFollowers(params: any) {
      return this._fetch('app.bsky.graph.getFollowers', params);
    }
    
    async listConvos(params: any) {
      return this._fetch('chat.bsky.convo.listConvos', params, 'GET', 'https://chat.bsky.social');
    }
    
    async uploadBlob(data: Uint8Array, opts: any) {
      const res = await fetch(this.service + '/xrpc/com.atproto.repo.uploadBlob', {
        method: 'POST',
        headers: {
          'content-type': opts.encoding,
          ...(this.session?.accessJwt ? { authorization: `Bearer ${this.session.accessJwt}` } : {}),
        },
        body: data as any,
      });
      if (!res.ok) throw new Error('uploadBlob failed');
      const resData = await res.json();
      return { data: resData };
    }
  }

  agent = new SimpleBrowserAgent({ service: 'https://bsky.social' });
  console.log('ensureAgent: SimpleBrowserAgent created');
  return agent;
}

export const login = async (identifier: string, password: string) => {
  try {
    console.log('login: attempting', identifier);
    const a = await ensureAgent();
    const res = await a.login({ identifier, password });
    console.log('login: success', res);
    
    // Save account info
    await saveAccount({
      identifier,
      password,
      handle: res.data.handle,
      did: res.data.did,
      avatar: res.data.avatar, // Might be undefined, that's fine
    });
    
    return res;
  } catch (err) {
    console.error('login: failed', err);
    throw err;
  }
};

export const getDid = () => agent?.session?.did;

export interface Account {
  identifier: string;
  password: string;
  handle: string;
  did: string;
  avatar?: string;
  active: boolean;
}

export const getAccounts = async (): Promise<Account[]> => {
  const accountsJson = await getItem('bsky_accounts');
  if (!accountsJson) return [];
  try {
    return JSON.parse(accountsJson);
  } catch (e) {
    return [];
  }
};

export const saveAccount = async (account: Omit<Account, 'active'>) => {
  const accounts = await getAccounts();
  const existingIndex = accounts.findIndex(a => a.did === account.did);
  
  const newAccounts = accounts.map(a => ({ ...a, active: false }));
  if (existingIndex >= 0) {
    newAccounts[existingIndex] = { ...account, active: true };
  } else {
    newAccounts.push({ ...account, active: true });
  }
  
  await setItem('bsky_accounts', JSON.stringify(newAccounts));
  // Also set legacy keys for compatibility if needed, or just rely on accounts
  await setItem('bsky_identifier', account.identifier);
  await setItem('bsky_password', account.password);
};

export const switchAccount = async (did: string) => {
  const accounts = await getAccounts();
  const account = accounts.find(a => a.did === did);
  if (!account) throw new Error('Account not found');
  
  const newAccounts = accounts.map(a => ({
    ...a,
    active: a.did === did
  }));
  
  await setItem('bsky_accounts', JSON.stringify(newAccounts));
  await setItem('bsky_identifier', account.identifier);
  await setItem('bsky_password', account.password);
  
  // Reset agent to force re-login with new credentials
  agent = null;
  await login(account.identifier, account.password);
};

export const removeAccount = async (did: string) => {
  const accounts = await getAccounts();
  const newAccounts = accounts.filter(a => a.did !== did);
  
  if (newAccounts.length > 0 && accounts.find(a => a.did === did)?.active) {
    newAccounts[0].active = true;
    await setItem('bsky_identifier', newAccounts[0].identifier);
    await setItem('bsky_password', newAccounts[0].password);
  } else if (newAccounts.length === 0) {
    await deleteItem('bsky_identifier');
    await deleteItem('bsky_password');
  }
  
  await setItem('bsky_accounts', JSON.stringify(newAccounts));
};

export const logout = async () => {
  const accounts = await getAccounts();
  const activeAccount = accounts.find(a => a.active);
  if (activeAccount) {
    await removeAccount(activeAccount.did);
  }
  agent = null;
};

export const getTimeline = async (cursor?: string) => {
  const a = await ensureAgent();
  const response = await a.getTimeline({ limit: 30, cursor });
  return { feed: response.data.feed, cursor: response.data.cursor };
};

/**
 * Specialized function for Chat/TalkRoom to get messages with guaranteed text extraction.
 */
export const getChatMessages = async (
  type: 'following' | 'feed' | 'list' | 'convo',
  uri?: string,
  cursor?: string,
  myDid?: string
): Promise<{ messages: any[]; cursor?: string }> => {
  const a = await ensureAgent();
  let response: any;

  if (type === 'following') {
    response = await a.getTimeline({ limit: 30, cursor });
  } else if (type === 'feed' && uri) {
    // Official BskyAgent doesn't have getFeed as a top-level convenience method
    if (typeof a.getFeed === 'function') {
      response = await a.getFeed({ feed: uri, limit: 30, cursor });
    } else {
      response = await a.app.bsky.feed.getFeed({ feed: uri, limit: 30, cursor });
    }
  } else if (type === 'list' && uri) {
    // Official BskyAgent doesn't have getListFeed as a top-level convenience method
    if (typeof a.getListFeed === 'function') {
      response = await a.getListFeed({ list: uri, limit: 30, cursor });
    } else {
      response = await a.app.bsky.feed.getListFeed({ list: uri, limit: 30, cursor });
    }
  } else if (type === 'convo' && uri) {
    const directFetch = async () => {
      const accessJwt = a.session?.accessJwt;
      if (!accessJwt) throw new Error('Authentication required for DM');

      const res = await fetch(`https://chat.bsky.social/xrpc/chat.bsky.convo.getMessages?convoId=${uri}&limit=30${cursor ? `&cursor=${cursor}` : ''}`, {
        headers: {
          authorization: `Bearer ${accessJwt}`,
        },
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      return { data };
    };

    try {
      try {
        if (a.chat?.bsky?.convo?.getMessages) {
          response = await a.chat.bsky.convo.getMessages(
            { convoId: uri, limit: 30, cursor },
            { headers: { 'atproto-proxy': 'did:web:api.bsky.chat' } }
          );
        } else {
          response = await directFetch();
        }
      } catch (e: any) {
        console.warn('SDK getMessages failed, trying directFetch:', e.message);
        response = await directFetch();
      }
      
      const messages = response.data?.messages || [];
      const mapped = messages.map((m: any) => {
        if (m.$type !== 'chat.bsky.convo.defs#messageView') return null;

        // Handle embeds in chat messages
        let images: any[] = [];
        let video: string | undefined;
        let external: any | undefined;
        if (m.embed) {
          if (m.embed.$type === 'app.bsky.embed.images#view') {
            images = m.embed.images.map((img: any) => ({ url: img.fullsize, aspectRatio: img.aspectRatio }));
          } else if (m.embed.$type === 'app.bsky.embed.video#view') {
            video = m.embed.playlist;
          } else if (m.embed.$type === 'app.bsky.embed.external#view') {
            external = m.embed.external;
          }
        }

        return {
          id: m.id,
          uri: m.id,
          cid: m.id,
          text: m.text,
          createdAt: new Date(m.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          date: m.sentAt.split('T')[0],
          indexedAt: m.sentAt, // Use sentAt as indexedAt for sorting
          isMine: m.sender?.did === myDid,
          authorDid: m.sender?.did,
          senderName: m.sender?.displayName || m.sender?.handle || 'Unknown',
          avatarUrl: m.sender?.avatar,
          images: images.length > 0 ? images : undefined,
          video,
          external: external ? {
            uri: external.uri,
            title: external.title,
            description: external.description,
            thumb: external.thumb,
          } : undefined,
        };
      }).filter(Boolean);
      
      // Bluesky chat.bsky.convo.getMessages returns messages in chronological order (oldest first).
      // We reverse it to [latest, ..., oldest] to match timeline behavior and work with inverted FlatList.
      return { messages: mapped.reverse(), cursor: response.data.cursor };
    } catch (error) {
      console.error('Failed to fetch convo messages:', error);
      return { messages: [] };
    }
  } else {
    return { messages: [] };
  }

  const feed = response.data.feed || [];
  const nextCursor = response.data.cursor;

  const extractText = (p: any): string => {
    if (!p) return '';
    // 1. Direct text property
    if (typeof p.text === 'string') return p.text;
    
    // 2. Record or Value text
    const record = p.record || {};
    const value = p.value || record.value || {};
    
    if (typeof record.text === 'string') return record.text;
    if (typeof value.text === 'string') return value.text;
    
    // 3. Rich text objects
    if (record.text && typeof record.text === 'object' && typeof record.text.text === 'string') return record.text.text;
    if (value.text && typeof value.text === 'object' && typeof value.text.text === 'string') return value.text.text;
    if (p.text && typeof p.text === 'object' && typeof p.text.text === 'string') return p.text.text;
    
    return '';
  };

  const mapped = feed.map((item: any) => {
    const { post, reason, reply } = item;
    if (!post) return null;

    const text = extractText(post);
    const record = post.record || post.record?.value || {};

    // Media
    let images: any[] = [];
    let video: string | undefined;
    let external: any | undefined;
    if (post.embed) {
      if (post.embed.$type === 'app.bsky.embed.images#view') {
        images = post.embed.images.map((img: any) => ({ url: img.fullsize, aspectRatio: img.aspectRatio }));
      } else if (post.embed.$type === 'app.bsky.embed.recordWithMedia#view') {
        if (post.embed.media?.$type === 'app.bsky.embed.images#view') {
          images = post.embed.media.images.map((img: any) => ({ url: img.fullsize, aspectRatio: img.aspectRatio }));
        } else if (post.embed.media?.$type === 'app.bsky.embed.video#view') {
          video = post.embed.media.playlist;
        } else if (post.embed.media?.$type === 'app.bsky.embed.external#view') {
          external = post.embed.media.external;
        }
      } else if (post.embed.$type === 'app.bsky.embed.video#view') {
        video = post.embed.playlist;
      } else if (post.embed.$type === 'app.bsky.embed.external#view') {
        external = post.embed.external;
      }
    }

    // Reply Parent
    let replyParent = undefined;
    if (reply?.parent) {
      replyParent = {
        uri: reply.parent.uri,
        cid: reply.parent.cid,
        senderName: reply.parent.author?.displayName || reply.parent.author?.handle || 'Unknown',
        text: extractText(reply.parent),
        avatarUrl: reply.parent.author?.avatar,
        author: reply.parent.author,
        record: reply.parent.record,
      };
    }

    // Quoted Post
    let quotedPost = undefined;
    // embed.record can be a ViewRecord or a RecordWithMedia
    const embed = post.embed;
    let qRecord = embed?.record?.record || embed?.record;
    
    if (qRecord?.$type === 'app.bsky.embed.record#viewRecord' || qRecord?.author) {
      // Extract images/video from quoted post's embeds
      let qImages: any[] = [];
      let qVideo: string | undefined;
      let qExternal: any | undefined;
      
      if (qRecord.embeds && qRecord.embeds.length > 0) {
        const qEmbed = qRecord.embeds[0];
        
        if (qEmbed.$type === 'app.bsky.embed.images#view') {
          qImages = qEmbed.images.map((img: any) => ({ url: img.fullsize, aspectRatio: img.aspectRatio }));
        } else if (qEmbed.$type === 'app.bsky.embed.video#view') {
          qVideo = qEmbed.playlist;
        } else if (qEmbed.$type === 'app.bsky.embed.external#view') {
          qExternal = qEmbed.external;
        }
      }
      
      quotedPost = {
        uri: qRecord.uri,
        cid: qRecord.cid,
        senderName: qRecord.author?.displayName || qRecord.author?.handle || 'Unknown',
        text: extractText(qRecord),
        avatarUrl: qRecord.author?.avatar,
        author: qRecord.author,
        record: qRecord.record || qRecord.value,
        images: qImages.length > 0 ? qImages : undefined,
        video: qVideo,
        external: qExternal ? {
          uri: qExternal.uri,
          title: qExternal.title,
          description: qExternal.description,
          thumb: qExternal.thumb,
        } : undefined,
      };
    }

    return {
      id: post.cid,
      uri: post.uri,
      cid: post.cid,
      author: post.author,
      record: record,
      text: text,
      createdAt: new Date(record.createdAt || post.indexedAt || new Date().toISOString()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      date: (record.createdAt || post.indexedAt || new Date().toISOString()).split('T')[0],
      isMine: post.author?.did === myDid,
      authorDid: post.author?.did,
      labels: post.labels,
      senderName: post.author?.displayName || post.author?.handle || 'Unknown',
      avatarUrl: post.author?.avatar,
      repostedBy: reason?.$type === 'app.bsky.feed.defs#reasonRepost' ? (reason.by?.displayName || reason.by?.handle) : undefined,
      images: images.length > 0 ? images : undefined,
      video,
      external: external ? {
        uri: external.uri,
        title: external.title,
        description: external.description,
        thumb: external.thumb,
      } : undefined,
      replyRoot: reply?.root ? { uri: reply.root.uri, cid: reply.root.cid } : undefined,
      replyParent,
      quotedPost,
      viewer: post.viewer ? { like: post.viewer.like, repost: post.viewer.repost } : undefined,
    };
  }).filter(Boolean);

  return { messages: mapped, cursor: nextCursor };
};

export const getPostThread = async (uri: string) => {
  const a = await ensureAgent();
  const response = await a.getPostThread({ uri });
  return response.data.thread;
};

const MAX_BLOB_SIZE = 1000000; // 1MB limit for Bluesky blobs

export const compressImageIfNeeded = async (uri: string): Promise<{ uri: string; size: number }> => {
  if (Platform.OS === 'web') {
    return { uri, size: 0 }; // Skip compression on web
  }
  let fileInfo = await FileSystem.getInfoAsync(uri);
  if (!fileInfo.exists) throw new Error('File does not exist');
  
  let currentUri = uri;
  let currentSize = (fileInfo as any).size;
  let quality = 0.8;

  while (currentSize > MAX_BLOB_SIZE && quality > 0.1) {
    const result = await ImageManipulator.manipulateAsync(
      currentUri,
      [{ resize: { width: 1200 } }], // Resize to a reasonable max width
      { compress: quality, format: ImageManipulator.SaveFormat.JPEG }
    );
    currentUri = result.uri;
    const newInfo = await FileSystem.getInfoAsync(currentUri);
    currentSize = (newInfo as any).size;
    quality -= 0.2;
  }

  return { uri: currentUri, size: currentSize };
};

export const uploadBlob = async (uri: string, mimeType: string) => {
  const a = await ensureAgent();
  let targetUri = uri;
  if (mimeType.startsWith('image/')) {
    const compressed = await compressImageIfNeeded(uri);
    targetUri = compressed.uri;
  }
  const response = await fetch(targetUri);
  const blob = await response.blob();
  const arrayBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = reject;
    reader.readAsArrayBuffer(blob);
  });
  return await a.uploadBlob(new Uint8Array(arrayBuffer), { encoding: mimeType });
};

export interface PostOptions {
  text: string;
  media?: { uri: string; mimeType: string; alt?: string }[];
  reply?: { root: { uri: string; cid: string }; parent: { uri: string; cid: string } };
  quote?: { uri: string; cid: string };
}

export const createPost = async (options: PostOptions) => {
  const a = await ensureAgent();
  
  console.log('📝 [createPost] Text:', options.text);
  
  // Load @atproto/api if not already loaded
  if (!RichText) {
    console.log('📝 [createPost] Loading @atproto/api...');
    await loadAtprotoApi();
  }
  
  // Create RichText to detect links, hashtags, and mentions
  let facets = undefined;
  if (RichText) {
    console.log('📝 [createPost] RichText available, detecting facets');
    try {
      const rt = new RichText({ text: options.text });
      await rt.detectFacets(a);
      facets = rt.facets;
      console.log('📝 [createPost] Facets detected:', JSON.stringify(facets, null, 2));
    } catch (e) {
      console.warn('📝 [createPost] RichText.detectFacets failed:', e);
      console.log('📝 [createPost] Falling back to manual facet detection');
      facets = detectFacetsManually(options.text);
    }
  } else {
    console.log('📝 [createPost] RichText not available, using manual facet detection');
    facets = detectFacetsManually(options.text);
  }
  
  if (facets && facets.length > 0) {
    console.log('📝 [createPost] Final facets:', JSON.stringify(facets, null, 2));
  }
  
  let embed: any = undefined;
  let mediaEmbed: any = undefined;
  let uploadedImageUrls: { url: string; aspectRatio?: { width: number; height: number } }[] = [];
  let uploadedVideoUrl: string | undefined;
  
  if (options.media && options.media.length > 0) {
    // Separate images and videos
    const images = options.media.filter(m => m.mimeType.startsWith('image/'));
    const videos = options.media.filter(m => m.mimeType.startsWith('video/'));

    if (images.length > 0) {
      const uploadedImages = await Promise.all(
        images.map(async (img) => {
          const { data } = await uploadBlob(img.uri, img.mimeType);
          const did = a.session?.did || getDid();
          const imageUrl = `https://cdn.bsky.app/img/feed_fullsize/plain/${did}/${data.blob.ref.$link}@jpeg`;
          uploadedImageUrls.push({ url: imageUrl, aspectRatio: undefined });
          return { image: data.blob, alt: img.alt || '' };
        }),
      );

      mediaEmbed = {
        $type: 'app.bsky.embed.images',
        images: uploadedImages,
      };
    }

    // Note: Bluesky video upload is complex and requires a separate service.
    // For now, we'll try to upload small videos as regular blobs if they are under the limit.
    if (videos.length > 0 && !mediaEmbed) {
      const { data } = await uploadBlob(videos[0].uri, videos[0].mimeType);
      const did = a.session?.did || getDid();
      uploadedVideoUrl = `https://video.bsky.app/watch/${did}/${data.blob.ref.$link}/playlist.m3u8`;
      mediaEmbed = {
        $type: 'app.bsky.embed.video',
        video: data.blob,
      };
    }
  }

  if (options.quote) {
    const recordEmbed = {
      $type: 'app.bsky.embed.record',
      record: {
        uri: options.quote.uri,
        cid: options.quote.cid,
      },
    };

    if (mediaEmbed) {
      embed = {
        $type: 'app.bsky.embed.recordWithMedia',
        record: recordEmbed,
        media: mediaEmbed,
      };
    } else {
      embed = recordEmbed;
    }
  } else {
    embed = mediaEmbed;
  }

  const result = await a.post({ 
    text: options.text, 
    facets, 
    embed, 
    reply: options.reply, 
    createdAt: new Date().toISOString() 
  });
  
  return {
    ...result,
    uploadedMedia: {
      images: uploadedImageUrls,
      video: uploadedVideoUrl,
    },
  };
};

export const post = async (text: string) => {
  await createPost({ text });
};

export const deletePost = async (uri: string) => {
  console.log('🗑️ [deletePost] Starting deletion for URI:', uri);
  const a = await ensureAgent();
  console.log('🗑️ [deletePost] Agent type:', a.constructor.name);
  console.log('🗑️ [deletePost] Session DID:', a.session?.did);
  
  const { repo, collection, rkey } = parseAtUri(uri);
  console.log('🗑️ [deletePost] Parsed URI:', { repo, collection, rkey });
  
  const targetRepo = repo || a.session?.did;
  
  if (!targetRepo || !rkey) {
    console.error('❌ [deletePost] Missing targetRepo or rkey');
    throw new Error('Invalid URI or session for deletion');
  }

  console.log('🗑️ [deletePost] Target params:', { targetRepo, collection: collection || 'app.bsky.feed.post', rkey });

  // Try the most direct way first (official SDK)
  if (a.com?.atproto?.repo?.deleteRecord) {
    console.log('🗑️ [deletePost] Using official SDK deleteRecord');
    try {
      const result = await a.com.atproto.repo.deleteRecord({
        repo: targetRepo,
        collection: collection || 'app.bsky.feed.post',
        rkey,
      });
      console.log('✅ [deletePost] SDK deleteRecord success:', result);
      return result;
    } catch (e) {
      console.error('❌ [deletePost] SDK deleteRecord failed:', e);
      throw e;
    }
  }
  
  // Fallback to agent method if available
  if (typeof a.deletePost === 'function') {
    console.log('🗑️ [deletePost] Using agent.deletePost method');
    try {
      const result = await a.deletePost(uri);
      console.log('✅ [deletePost] agent.deletePost success:', result);
      return result;
    } catch (e) {
      console.error('❌ [deletePost] agent.deletePost failed:', e);
      throw e;
    }
  }

  // Fallback to direct fetch (SimpleBrowserAgent)
  console.log('🗑️ [deletePost] Using _fetch fallback');
  try {
    const result = await a._fetch('com.atproto.repo.deleteRecord', {
      repo: targetRepo,
      collection: collection || 'app.bsky.feed.post',
      rkey
    }, 'POST');
    console.log('✅ [deletePost] _fetch success:', result);
    return result;
  } catch (e) {
    console.error('❌ [deletePost] _fetch failed:', e);
    throw e;
  }
};

export const like = async (uri: string, cid: string) => {
  const a = await ensureAgent();
  return await a.like(uri, cid);
};

export const unlike = async (likeUri: string) => {
  const a = await ensureAgent();
  return await a.deleteLike(likeUri);
};

export const repost = async (uri: string, cid: string) => {
  const a = await ensureAgent();
  return await a.repost(uri, cid);
};

export const unrepost = async (repostUri: string) => {
  const a = await ensureAgent();
  return await a.deleteRepost(repostUri);
};

export const follow = async (did: string) => {
  const a = await ensureAgent();
  return await a.follow(did);
};

export const unfollow = async (followUri: string) => {
  const a = await ensureAgent();
  return await a.deleteFollow(followUri);
};

export const getPost = async (uri: string) => {
  const a = await ensureAgent();
  const response = await a.getPostThread({ uri });
  return response.data.thread;
};

export const getSavedFeeds = async () => {
  try {
    const a = await ensureAgent();
    const response = await a.app.bsky.actor.getPreferences();
    
    // Try V2 first (newer Bluesky accounts/apps use this)
    const v2Pref = response.data.preferences.find(
      (pref: any) => pref.$type === 'app.bsky.actor.defs#savedFeedsPrefV2'
    ) as any;
    
    const v1Pref = response.data.preferences.find(
      (pref: any) => pref.$type === 'app.bsky.actor.defs#savedFeedsPref'
    ) as any;

    let feedUris: string[] = [];
    let pinnedUris: string[] = [];

    if (v2Pref && v2Pref.items) {
      feedUris = v2Pref.items
        .filter((item: any) => item.type === 'feed')
        .map((item: any) => item.value);
      pinnedUris = v2Pref.items
        .filter((item: any) => item.type === 'feed' && item.pinned)
        .map((item: any) => item.value);
    } else if (v1Pref && v1Pref.saved) {
      feedUris = v1Pref.saved;
      pinnedUris = v1Pref.pinned || [];
    }

    if (feedUris.length > 0) {
      const feedsResponse = await a.app.bsky.feed.getFeedGenerators({
        feeds: feedUris.slice(0, 25),
      });
      return (feedsResponse.data.feeds || []).map((f: any) => ({
        ...f,
        pinned: pinnedUris.includes(f.uri),
      }));
    }

    return [];
  } catch (error) {
    console.error('Failed to fetch saved feeds:', error);
    return [];
  }
};

export const getLists = async (actor?: string) => {
  try {
    const a = await ensureAgent();
    const targetActor = actor || a.session?.did || '';
    
    // Get owned lists
    const ownedListsRes = await a.app.bsky.graph.getLists({
      actor: targetActor,
    });
    const ownedLists = ownedListsRes.data.lists || [];

    // If it's not the current user, we might not want to fetch their saved lists from prefs
    if (targetActor !== a.session?.did) {
      return ownedLists;
    }

    // Get saved lists from preferences (V2)
    const prefsRes = await a.app.bsky.actor.getPreferences();
    const v2Pref = prefsRes.data.preferences.find(
      (pref: any) => pref.$type === 'app.bsky.actor.defs#savedFeedsPrefV2'
    ) as any;

    let savedListUris: string[] = [];
    let pinnedListUris: string[] = [];
    if (v2Pref && v2Pref.items) {
      savedListUris = v2Pref.items
        .filter((item: any) => item.type === 'list')
        .map((item: any) => item.value);
      pinnedListUris = v2Pref.items
        .filter((item: any) => item.type === 'list' && item.pinned)
        .map((item: any) => item.value);
    }

    // Filter out lists we already have (owned)
    const ownedUris = new Set(ownedLists.map((l: any) => l.uri));
    const extraListUris = savedListUris.filter(uri => !ownedUris.has(uri));

    let allLists = ownedLists.map((l: any) => ({
      ...l,
      pinned: pinnedListUris.includes(l.uri),
    }));

    if (extraListUris.length > 0) {
      try {
        // Fetch extra lists (limit to 25 for safety)
        const extraLists = await Promise.all(
          extraListUris.slice(0, 25).map(async (uri) => {
            try {
              const res = await a.app.bsky.graph.getList({ list: uri });
              return {
                ...res.data.list,
                pinned: pinnedListUris.includes(uri),
              };
            } catch (e) {
              return null;
            }
          })
        );
        allLists = [...allLists, ...extraLists.filter(Boolean)];
      } catch (error) {
        console.error('Failed to fetch extra lists:', error);
      }
    }

    return allLists;
  } catch (error) {
    console.error('Failed to fetch lists:', error);
    return [];
  }
};

export const searchFeeds = async (query: string) => {
  try {
    console.log('[searchFeeds] Searching for feeds (query length:', query.length, 'chars)');
    const a = await ensureAgent();
    const response = await a.app.bsky.unspecced.getPopularFeedGenerators({
      query: query,
      limit: 20,
    });
    console.log('[searchFeeds] Found', response.data.feeds?.length || 0, 'feeds');
    return response.data.feeds || [];
  } catch (error) {
    console.error('[searchFeeds] Failed to search feeds:', error);
    throw new Error('Failed to search feeds. Please try again.');
  }
};

export const saveFeed = async (feedUri: string) => {
  try {
    console.log('[saveFeed] Attempting to save feed');
    const a = await ensureAgent();
    const prefsRes = await a.app.bsky.actor.getPreferences();
    const preferences = prefsRes.data.preferences;

    // Find or create V2 saved feeds preference
    let v2Pref = preferences.find(
      (pref: any) => pref.$type === 'app.bsky.actor.defs#savedFeedsPrefV2'
    ) as any;

    if (!v2Pref) {
      v2Pref = {
        $type: 'app.bsky.actor.defs#savedFeedsPrefV2',
        items: [],
      };
    }

    // Check if feed is already saved
    const existingItem = v2Pref.items?.find(
      (item: any) => item.type === 'feed' && item.value === feedUri
    );

    if (existingItem) {
      console.log('[saveFeed] Feed already saved, skipping duplicate');
      return; // Feed already saved, nothing to do
    }

    // Add new feed
    v2Pref.items = [
      ...(v2Pref.items || []),
      {
        type: 'feed',
        value: feedUri,
        pinned: false,
      },
    ];

    // Update preferences
    const updatedPreferences = preferences.filter(
      (pref: any) => pref.$type !== 'app.bsky.actor.defs#savedFeedsPrefV2'
    );
    updatedPreferences.push(v2Pref);

    await a.app.bsky.actor.putPreferences({ preferences: updatedPreferences });
    console.log('[saveFeed] Feed saved successfully');
  } catch (error) {
    console.error('[saveFeed] Failed to save feed:', error);
    throw new Error('Failed to save feed. Please try again.');
  }
};

export const removeFeed = async (feedUri: string) => {
  try {
    console.log('[removeFeed] Attempting to remove feed');
    const a = await ensureAgent();
    const prefsRes = await a.app.bsky.actor.getPreferences();
    const preferences = prefsRes.data.preferences;

    // Find V2 saved feeds preference
    const v2Pref = preferences.find(
      (pref: any) => pref.$type === 'app.bsky.actor.defs#savedFeedsPrefV2'
    ) as any;

    if (!v2Pref || !v2Pref.items) {
      console.log('[removeFeed] No saved feeds to remove');
      return;
    }

    // Remove the feed
    const newItems = v2Pref.items.filter(
      (item: any) => !(item.type === 'feed' && item.value === feedUri)
    );

    if (newItems.length === v2Pref.items.length) {
      console.log('[removeFeed] Feed not found in saved feeds');
      return;
    }

    v2Pref.items = newItems;

    // Update preferences
    const updatedPreferences = preferences.filter(
      (pref: any) => pref.$type !== 'app.bsky.actor.defs#savedFeedsPrefV2'
    );
    updatedPreferences.push(v2Pref);

    await a.app.bsky.actor.putPreferences({ preferences: updatedPreferences });
    console.log('[removeFeed] Feed removed successfully');
  } catch (error) {
    console.error('[removeFeed] Failed to remove feed:', error);
    throw new Error('Failed to remove feed. Please try again.');
  }
};

export const getFeed = async (feedUri: string, cursor?: string) => {
  const a = await ensureAgent();
  const params = {
    feed: feedUri,
    limit: 30,
    cursor,
  };
  const response = await (typeof a.getFeed === 'function' 
    ? a.getFeed(params) 
    : a.app.bsky.feed.getFeed(params));
  return {
    feed: response.data.feed,
    cursor: response.data.cursor,
  };
};

export const getListFeed = async (listUri: string, cursor?: string) => {
  const a = await ensureAgent();
  const params = {
    list: listUri,
    limit: 30,
    cursor,
  };
  const response = await (typeof a.getListFeed === 'function' 
    ? a.getListFeed(params) 
    : a.app.bsky.feed.getListFeed(params));
  return {
    feed: response.data.feed,
    cursor: response.data.cursor,
  };
};

export const getPosts = async (uris: string[]) => {
  const a = await ensureAgent();
  const response = await a.getPosts({ uris });
  return response.data.posts;
};

export const listNotifications = async (cursor?: string) => {
  const a = await ensureAgent();
  const response = await a.listNotifications({
    limit: 30,
    cursor,
  });
  return {
    notifications: response.data.notifications,
    cursor: response.data.cursor,
  };
};

export const getProfile = async (actor: string) => {
  const a = await ensureAgent();
  const response = await a.getProfile({ actor });
  return response.data;
};

export const updateProfile = async (updates: { displayName?: string; description?: string; avatar?: any; banner?: any }) => {
  const a = await ensureAgent();
  const did = a.session?.did;
  const accessJwt = a.session?.accessJwt || a.accessJwt;
  if (!did || !accessJwt) throw new Error('Not logged in');

  const serviceUrl = a.service?.toString() || 'https://bsky.social';

  // Fetch existing record to preserve avatar/banner blob refs
  let existingRecord: any = null;
  try {
    const resRecord = await fetch(
      `${serviceUrl}/xrpc/com.atproto.repo.getRecord?repo=${encodeURIComponent(did)}&collection=app.bsky.actor.profile&rkey=self`,
      {
        headers: { authorization: `Bearer ${accessJwt}` },
      }
    );
    if (resRecord.ok) {
      const data = await resRecord.json();
      existingRecord = data?.value || null;
    }
  } catch (err) {
    console.warn('updateProfile: failed to fetch existing record, proceeding without it', err);
  }

  const record: any = {
    $type: 'app.bsky.actor.profile',
    displayName: updates.displayName !== undefined ? updates.displayName : existingRecord?.displayName,
    description: updates.description !== undefined ? updates.description : existingRecord?.description,
  };

  // Preserve or set avatar/banner blobs. If none, omit field to avoid invalid URL string.
  if (updates.avatar !== undefined) {
    record.avatar = updates.avatar;
  } else if (existingRecord?.avatar) {
    record.avatar = existingRecord.avatar;
  }

  if (updates.banner !== undefined) {
    record.banner = updates.banner;
  } else if (existingRecord?.banner) {
    record.banner = existingRecord.banner;
  }

  const res = await fetch(serviceUrl + '/xrpc/com.atproto.repo.putRecord', {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${accessJwt}`,
    },
    body: JSON.stringify({
      repo: did,
      collection: 'app.bsky.actor.profile',
      rkey: 'self',
      record,
    }),
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to update profile');
  }

  return await res.json();
};

export const getAuthorFeed = async (actor: string, cursor?: string) => {
  const a = await ensureAgent();
  const response = await a.getAuthorFeed({ actor, limit: 30, cursor });
  return {
    feed: response.data.feed,
    cursor: response.data.cursor,
  };
};

export const getConvos = async (cursor?: string) => {
  const a = await ensureAgent();
  
  const directFetch = async () => {
    // For @atproto/api 0.18.x, session info is in a.session
    const accessJwt = a.session?.accessJwt;
    if (!accessJwt) throw new Error('Authentication required for DM (No Access Token)');
    
    console.log('getConvos: using directFetch fallback to https://chat.bsky.social');
    const res = await fetch(`https://chat.bsky.social/xrpc/chat.bsky.convo.listConvos?limit=30${cursor ? `&cursor=${cursor}` : ''}`, {
      headers: {
        authorization: `Bearer ${accessJwt}`,
      },
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      const msg = errorData.message || errorData.error || `HTTP ${res.status}`;
      console.error('DM directFetch failed:', msg);
      throw new Error(msg);
    }
    const data = await res.json();
    return { data };
  };

  try {
    let response: any;
    try {
      if (a.chat?.bsky?.convo?.listConvos) {
        // Official SDK way for @atproto/api 0.18.x using the mandatory proxy header
        response = await a.chat.bsky.convo.listConvos(
          { limit: 30, cursor },
          { headers: { 'atproto-proxy': 'did:web:api.bsky.chat' } }
        );
      } else {
        response = await directFetch();
      }
    } catch (e: any) {
      console.warn('SDK getConvos failed, trying directFetch:', e.message);
      response = await directFetch();
    }

    return {
      convos: response.data?.convos || [],
      cursor: response.data?.cursor,
    };
  } catch (error: any) {
    console.error('Failed to fetch convos:', error.message || error);
    return { convos: [] };
  }
};

export const getConvoForMembers = async (members: string[]) => {
  const a = await ensureAgent();
  
  const directFetch = async () => {
    const query = members.map(m => `members=${encodeURIComponent(m)}`).join('&');
    const accessJwt = a.session?.accessJwt;
    if (!accessJwt) throw new Error('Authentication required for DM');
    
    const res = await fetch(`https://chat.bsky.social/xrpc/chat.bsky.convo.getConvoForMembers?${query}`, {
      headers: {
        authorization: `Bearer ${accessJwt}`,
      },
    });
    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `HTTP ${res.status}`);
    }
    const data = await res.json();
    return { data };
  };

  try {
    let response: any;
    try {
      if (a.chat?.bsky?.convo?.getConvoForMembers) {
        response = await a.chat.bsky.convo.getConvoForMembers(
          { members },
          { headers: { 'atproto-proxy': 'did:web:api.bsky.chat' } }
        );
      } else {
        response = await directFetch();
      }
    } catch (e: any) {
      console.warn('SDK getConvoForMembers failed, trying directFetch:', e.message);
      response = await directFetch();
    }

    if (!response?.data?.convo) {
      throw new Error('Conversation not found in response');
    }
    return response.data.convo;
  } catch (error: any) {
    console.error('Failed to get convo for members:', error.message || error);
    throw error;
  }
};

export const sendConvoMessage = async (convoId: string, text: string) => {
  const a = await ensureAgent();
  try {
    let response: any;
    const params = {
      convoId,
      message: {
        text,
      },
    };
    
    const directFetch = async () => {
      const accessJwt = a.session?.accessJwt;
      if (!accessJwt) throw new Error('Authentication required for DM');

      const res = await fetch('https://chat.bsky.social/xrpc/chat.bsky.convo.sendMessage', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          authorization: `Bearer ${accessJwt}`,
        },
        body: JSON.stringify(params),
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      return { data };
    };

    try {
      if (a.chat?.bsky?.convo?.sendMessage) {
        response = await a.chat.bsky.convo.sendMessage(
          params,
          { headers: { 'atproto-proxy': 'did:web:api.bsky.chat' } }
        );
      } else {
        response = await directFetch();
      }
    } catch (e: any) {
      console.warn('SDK sendMessage failed, trying directFetch:', e.message);
      response = await directFetch();
    }
    return response.data;
  } catch (error: any) {
    console.error('Failed to send convo message:', error.message || error);
    throw error;
  }
};

export async function searchPosts(query: string, cursor?: string) {
  const a = await ensureAgent();
  const res = await a.app.bsky.feed.searchPosts({ q: query, cursor, limit: 25 });
  return res.data;
}

export async function searchActors(query: string, cursor?: string) {
  const a = await ensureAgent();
  const res = await a.app.bsky.actor.searchActors({ q: query, cursor, limit: 25 });
  return res.data;
}

export const getFollows = async (actor: string, cursor?: string) => {
  const a = await ensureAgent();
  const response = await a.getFollows({ actor, limit: 50, cursor });
  return {
    follows: response.data.follows,
    cursor: response.data.cursor,
  };
};

export const getFollowers = async (actor: string, cursor?: string) => {
  const a = await ensureAgent();
  const response = await a.getFollowers({ actor, limit: 50, cursor });
  return {
    followers: response.data.followers,
    cursor: response.data.cursor,
  };
};

export const getLikes = async (uri: string, cursor?: string) => {
  const a = await ensureAgent();
  const response = await a.app.bsky.feed.getLikes({ uri, limit: 50, cursor });
  return {
    likes: response.data.likes,
    cursor: response.data.cursor,
  };
};

export const getRepostedBy = async (uri: string, cursor?: string) => {
  const a = await ensureAgent();
  const response = await a.app.bsky.feed.getRepostedBy({ uri, limit: 50, cursor });
  return {
    repostedBy: response.data.repostedBy,
    cursor: response.data.cursor,
  };
};

export const getQuotes = async (uri: string, cursor?: string) => {
  const a = await ensureAgent();
  const response = await a.app.bsky.feed.getQuotes({ uri, limit: 50, cursor });
  return {
    posts: response.data.posts,
    cursor: response.data.cursor,
  };
};

// Mute/Unmute Actor
export const muteActor = async (actor: string) => {
  const a = await ensureAgent();
  const response = await a._fetch('app.bsky.graph.muteActor', { actor }, 'POST');
  return response.data;
};

export const unmuteActor = async (actor: string) => {
  const a = await ensureAgent();
  const response = await a._fetch('app.bsky.graph.unmuteActor', { actor }, 'POST');
  return response.data;
};

// Block/Unblock Actor
export const blockActor = async (actor: string) => {
  const a = await ensureAgent();
  const did = getDid();
  if (!did) throw new Error('Not logged in');
  
  const response = await a._fetch('com.atproto.repo.createRecord', {
    repo: did,
    collection: 'app.bsky.graph.block',
    record: {
      subject: actor,
      createdAt: new Date().toISOString(),
    }
  }, 'POST');
  return response.data;
};

export const unblockActor = async (blockRecordUri: string) => {
  const a = await ensureAgent();
  const parts = blockRecordUri.split('/');
  const rkey = parts[parts.length - 1];
  const did = getDid();
  if (!did) throw new Error('Not logged in');
  
  const response = await a._fetch('com.atproto.repo.deleteRecord', {
    repo: did,
    collection: 'app.bsky.graph.block',
    rkey
  }, 'POST');
  return response.data;
};

// Get blocks list
export const getBlocks = async (cursor?: string) => {
  const a = await ensureAgent();
  const response = await a._fetch('app.bsky.graph.getBlocks', { limit: 100, cursor });
  return {
    blocks: response.data.blocks,
    cursor: response.data.cursor,
  };
};

// Get mutes list
export const getMutes = async (cursor?: string) => {
  const a = await ensureAgent();
  const response = await a._fetch('app.bsky.graph.getMutes', { limit: 100, cursor });
  return {
    mutes: response.data.mutes,
    cursor: response.data.cursor,
  };
};
